<!--
  Settings → Notifications (administrators only): everything about the mails
  the ERP sends, in one place, since 2026-10-18.

    - Automatic mails   what leaves on its own, when, to whom (parts/AutomaticMails.vue)
    - Mail check        the mail-tester.com score of the quote and invoice mails (parts/MailCheck.vue)
    - Quote email links the catalogue and data-sheet links of the quote mail (parts/QuoteEmailLinks.vue)
    - History           every mail sent, to read or send again (parts/MailHistory.vue)

  These were spread over three pages: Dependency Sync (the first two), Quote
  Email Links and Mail History. Their old addresses redirect here (router.js).
  The robots stay in Settings → Dependency Sync, with the settings of their own
  mail. The tab is in the URL (?tab=), so a link can open one directly.

  The first two tabs load with the page (their state feeds the tab badges);
  the last two only when first opened, then stay mounted: their popups live in
  document.body (Vuesax) and must not be destroyed while open.
-->
<template>
  <div class="hv2 notif-page">
    <div class="hv2-nav nt-nav" role="tablist">
      <div v-for="t in TABS" :key="t.key" class="hv2-nav-item" :class="{ 'is-active': tab === t.key }"
           role="tab" tabindex="0" :aria-selected="tab === t.key ? 'true' : 'false'"
           @click="setTab(t.key)" @keyup.enter="setTab(t.key)">
        <feather-icon :icon="t.icon" svgClasses="w-4 h-4" />{{ t.label }}
        <span v-if="t.key === 'mails' && mailsFailed" class="hv2-nav-count" :title="`${mailsFailed} mail(s) failed the last time`">{{ mailsFailed }}</span>
        <span v-if="t.key === 'mail-check' && mcAlert" class="hv2-nav-count" title="The last mail check is not good">!</span>
      </div>
      <div class="hv2-nav-spacer" />
      <a :href="$router.resolve({ path: '/settings/dependency-sync' }).href" class="hv2-nav-note nt-robots"
         @click.exact.prevent="$router.push({ path: '/settings/dependency-sync' })">
        <feather-icon icon="RefreshCwIcon" svgClasses="w-3 h-3" /> Robot mails: Dependency Sync
      </a>
    </div>

    <div v-show="tab === 'mails'">
      <automatic-mails ref="mails" @state="mailsFailed = $event.failed" />
    </div>
    <div v-show="tab === 'mail-check'">
      <mail-check @state="mcState = $event" @saved="reloadMails" />
    </div>
    <div v-if="visited['quote-links']" v-show="tab === 'quote-links'">
      <quote-email-links />
    </div>
    <div v-if="visited.history" v-show="tab === 'history'">
      <mail-history />
    </div>
  </div>
</template>

<script>
import AutomaticMails from './parts/AutomaticMails'
import MailCheck from './parts/MailCheck'
import QuoteEmailLinks from './parts/QuoteEmailLinks'
import MailHistory from './parts/MailHistory'

const TABS = [
  { key: 'mails', label: 'Automatic mails', icon: 'MailIcon' },
  { key: 'mail-check', label: 'Mail check', icon: 'CheckSquareIcon' },
  { key: 'quote-links', label: 'Quote email links', icon: 'LinkIcon' },
  { key: 'history', label: 'History', icon: 'ArchiveIcon' }
]

export default {
  components: { AutomaticMails, MailCheck, QuoteEmailLinks, MailHistory },
  data () {
    return {
      TABS,
      tab: 'mails',
      visited: { 'mails': true, 'mail-check': true, 'quote-links': false, 'history': false },
      mailsFailed: 0,
      mcState: null
    }
  },
  computed: {
    mcAlert () { return !!(this.mcState && this.mcState.tone === 'danger') }
  },
  methods: {
    setTab (key) {
      this.tab = key
      this.$set(this.visited, key, true)
      this.$router.replace({ query: key === 'mails' ? {} : { tab: key } }).catch(() => {})
    },
    reloadMails () { if (this.$refs.mails) this.$refs.mails.loadMails() }
  },
  created () {
    const roles = String(localStorage.getItem('login-roles') || '').toLowerCase()
    if (!roles.includes('admin')) {
      this.$vs.notify({
        title: 'Denied',
        text: 'This page is for administrators',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
        position: 'bottom-right',
        time: 8000
      })
      this.$router.push({ path: '/dashboard/analytics' })
      return
    }
    const q = (this.$route.query || {}).tab
    if (TABS.some((t) => t.key === q)) {
      this.tab = q
      this.visited[q] = true
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/modules/harvesting2.scss";

.notif-page {
  .nt-nav { margin-bottom: 18px; }
  .nt-nav .hv2-nav-item .feather-icon { margin-right: 6px; }
  .nt-robots { display: inline-flex; align-items: center; gap: 4px; color: var(--hv-muted); }
  .nt-robots:hover { color: var(--hv-sage-ink); }

  /* Shared by the tabs (same classes as Dependency Sync had). */
  .sumal-settings { margin-top: .5rem; }
  .sumal-settings-row { display: flex; align-items: center; margin-bottom: .75rem; }
  .sumal-settings-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 0; margin-top: .5rem; }
  .sumal-mail-editor,
  .qel-editor { margin-top: 1rem; padding: 1rem; border: 1px solid var(--hv-rule-strong); border-radius: 8px; }
  .sumal-check-actions { white-space: nowrap; }
  .sumal-mc-score { margin-bottom: .25rem; }
  .sumal-mc-issues { padding-left: 1.2rem; margin: 0; }
  .sumal-mc-issues li { margin-bottom: .4rem; }
  .hv2-kv-v .hv2-pill { vertical-align: middle; }

  /* Quote email links */
  .qel-url { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .qel-preview { padding: 10px 12px; border-radius: 6px; background: var(--hv-surface-2); }

  /* History */
  .mh-filters { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 0; }
  .mh-filter { min-width: 150px; display: inline-block; }
  .mh-ellipsis { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mh-actions { display: flex; flex-wrap: nowrap; }
  .mh-pager { display: flex; align-items: center; justify-content: flex-end; margin-top: 14px; }

  @media (max-width: 767px) {
    // The filters of History take the whole width and stack, instead of
    // pushing the page sideways (hv2-sec-actions does not shrink).
    .hv2-sec-actions { flex: 1 1 100%; min-width: 0; }
    .mh-filters > * { flex: 1 1 100%; margin-right: 0 !important; }
  }
}

/* The History popup lives in document.body (Vuesax), outside .notif-page. */
.vs-popup .hv2 .mh-v { font-size: .9rem; font-weight: 500; word-break: break-word; }
.vs-popup .mh-body { width: 100%; min-height: 360px; border: 1px solid rgba(128, 128, 128, .25); border-radius: 6px; background: #fff; }
.vs-popup .mh-sendto { margin-top: 1rem; padding: 12px; border: 1px solid rgba(128, 128, 128, .25); border-radius: 8px; }
</style>
